import { useMemo, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  themeQuartz,
  type ColDef,
  type GridReadyEvent,
  type RowClickedEvent,
  type GridApi,
} from 'ag-grid-community';
import { registerAgGridModules } from './agGridSetup';
import { AG_GRID_LOCALE_KR } from './agGridLocale';
import { LoadingState, ErrorState, EmptyState } from '../common/States';

registerAgGridModules();

/**
 * 프로젝트 공통 테마 (Quartz 기반, primary 컬러/폰트 일치화)
 */
const mungtodayTheme = themeQuartz.withParams({
  fontFamily:
    "Pretendard, Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif",
  headerFontWeight: 600,
  headerBackgroundColor: '#f9fafb',
  headerTextColor: '#374151',
  oddRowBackgroundColor: '#ffffff',
  rowHoverColor: '#fef7f0', // primary-50
  selectedRowBackgroundColor: '#fdeee0', // primary-100
  borderColor: '#e5e7eb',
  accentColor: '#ed8432', // primary-500
  rowBorder: { style: 'solid', width: 1, color: '#f3f4f6' },
  headerColumnBorder: false,
  cellHorizontalPadding: 12,
  spacing: 6,
  fontSize: 13,
  headerHeight: 40,
  rowHeight: 44,
  wrapperBorder: false,
});

export interface DataGridProps<T> {
  /** 컬럼 정의 (ag-grid ColDef) */
  columns: ColDef<T>[];
  /** 행 데이터 */
  data: T[];
  /** 행 키 추출 (서버 페이지네이션 시 row 식별용) */
  getRowId?: (row: T) => string;
  /** 로딩 상태 (초기 로드) */
  loading?: boolean;
  /** 백그라운드 재요청 중 */
  fetching?: boolean;
  /** 에러 */
  error?: unknown;
  /** 재시도 */
  onRetry?: () => void;
  /** 행 클릭 */
  onRowClick?: (row: T) => void;
  /** 행 높이 (기본 44) */
  rowHeight?: number;
  /** 헤더 높이 (기본 40) */
  headerHeight?: number;
  /** 빈 데이터 메시지 */
  emptyMessage?: string;
  /** 페이지네이션 (true = ag-grid 내장 / false = 외부 페이지네이션) — 기본 false */
  paginated?: boolean;
  /** ag-grid 내장 페이지네이션 사용 시 페이지 크기 */
  paginationPageSize?: number;
  /** 행 다중 선택 활성화 (체크박스) */
  selectable?: boolean;
  /** 선택 변경 콜백 */
  onSelectionChange?: (rows: T[]) => void;
  /** 외부에서 그리드 API 접근하고 싶을 때 */
  onGridReady?: (api: GridApi<T>) => void;
  /** 컨테이너 추가 클래스 */
  className?: string;
}

/**
 * ag-grid Community 기반 공통 DataGrid
 * - autoHeight 도메인: 페이지 전체가 단일 세로 스크롤 축을 가지도록
 * - 한국어 locale, mungtoday 테마, 공통 상태(로딩/에러/빈) 처리
 * - 행 클릭 / 다중 선택 / 외부 페이지네이션 지원
 */
export function DataGrid<T>({
  columns,
  data,
  getRowId,
  loading = false,
  fetching = false,
  error,
  onRetry,
  onRowClick,
  rowHeight = 44,
  headerHeight = 40,
  emptyMessage = '검색 조건에 맞는 데이터가 없습니다.',
  paginated = false,
  paginationPageSize = 20,
  selectable = false,
  onSelectionChange,
  onGridReady,
  className = '',
}: DataGridProps<T>) {
  const apiRef = useRef<GridApi<T> | null>(null);

  const defaultColDef = useMemo<ColDef<T>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    [],
  );

  const handleGridReady = (e: GridReadyEvent<T>) => {
    apiRef.current = e.api;
    onGridReady?.(e.api);
  };

  const handleRowClicked = (e: RowClickedEvent<T>) => {
    if (onRowClick && e.data) onRowClick(e.data);
  };

  // 데이터 갱신 시 행 식별을 위한 getRowId 어댑터
  const getRowIdAdapter = useMemo(() => {
    if (!getRowId) return undefined;
    return (params: { data: T }) => getRowId(params.data);
  }, [getRowId]);

  useEffect(() => {
    return () => {
      apiRef.current = null;
    };
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (!data || data.length === 0) return <EmptyState message={emptyMessage} />;

  return (
    <div className={`relative ${className}`}>
      <div
        className="overflow-hidden rounded-lg border border-gray-200 bg-white"
        // autoHeight 도메인을 쓰므로 컨테이너 자체 높이는 자동
      >
        <AgGridReact<T>
          theme={mungtodayTheme}
          columnDefs={columns}
          rowData={data}
          defaultColDef={defaultColDef}
          getRowId={getRowIdAdapter}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          domLayout="autoHeight"
          animateRows
          suppressCellFocus
          // 행 클릭
          onRowClicked={handleRowClicked}
          // 선택
          rowSelection={
            selectable
              ? { mode: 'multiRow', checkboxes: true, headerCheckbox: true }
              : undefined
          }
          onSelectionChanged={
            onSelectionChange
              ? () => {
                  const rows = apiRef.current?.getSelectedRows() ?? [];
                  onSelectionChange(rows);
                }
              : undefined
          }
          // 페이지네이션
          pagination={paginated}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginated ? [10, 20, 50, 100] : false}
          // 로케일
          localeText={AG_GRID_LOCALE_KR}
          onGridReady={handleGridReady}
        />
      </div>
      {fetching && (
        <div className="pointer-events-none absolute right-3 top-3 rounded-full bg-white px-3 py-1 text-xs text-gray-600 shadow">
          불러오는 중...
        </div>
      )}
    </div>
  );
}
